import { Fragment, useEffect, useState, type CSSProperties } from 'react'
import { AddEntrenamientoModal } from '../AddEntrenamientoModal'
import { useLang } from '../../i18n/LangProvider'
import { useAsistenciaApi, ApiError } from '../api'
import { mesLabel, diaSemanaLabel, Lang, t as tBase } from '../i18n'
import { mesesTemporada } from '../temporadaMeses'
import { t, estadoLabel, presenciasF11, ESTADOS_F11, EstadoF11, umbralF11, UMBRAL_COLOR } from './i18n'
import { ContextMenuEstado } from './ContextMenuEstado'
import { EstadoDiaMenu } from '../EstadoDiaMenu'
import { FichaOverlayF11 } from './FichaOverlayF11'
import styles from './AsistenciaF11Tab.module.css'
import { EstadoChip } from './EstadoChip'
import { AnadirMiembroBotones, Grupo } from '../roles'
import { Spinner } from '../../styles/Spinner'
import { useIsMobile } from '../../styles/useIsMobile'
import { useScrollActiveIntoView } from '../../styles/useScrollActiveIntoView'
import { DiaHeader } from '../DiaHeader'

interface Sesion {
  id: string
  fecha: string
  numero: number | null
  tipo: string
  origen: string
}

interface Miembro {
  id: string
  grupo: string
  rol_entrenador: string | null
  orden: number
  persona: { nombre: string; alias: string | null; foto_url: string | null }
  registros: { sesion_id: string; estado: string | null; nota: string | null }[]
  contadores: Record<string, number>
  porcentaje_mes: number | null
  porcentaje_ano: number | null
}

interface AsistenciaResponse {
  sesiones: Sesion[]
  miembros: Miembro[]
}

const CONTADOR_COLS: EstadoF11[] = ['EM', 'RC', 'LS', 'EN', 'TR', 'EX', 'VA', 'OT', 'NJ']

const GRUPOS: { key: string; labelKey: 'conFicha' | 'sinFicha' | 'entrenadores' }[] = [
  { key: 'con_ficha', labelKey: 'conFicha' },
  { key: 'sin_ficha', labelKey: 'sinFicha' },
  { key: 'entrenador', labelKey: 'entrenadores' },
]

function diaNumero(fechaISO: string): number {
  return Number(fechaISO.slice(8, 10))
}

function fechaCorta(fechaISO: string): string {
  return fechaISO.slice(0, 10)
}

function mesActualISO(): string {
  const now = new Date()
  return `${now.getUTCFullYear()}-${String(now.getUTCMonth() + 1).padStart(2, '0')}`
}

function pctLabel(pct: number | null): string {
  return pct == null ? '--' : `${pct}%`
}

function PctPill({ pct }: { pct: number | null }) {
  const nivel = umbralF11(pct == null ? '--' : pct)
  return (
    <span className="pill" style={{ color: UMBRAL_COLOR[nivel] }}>
      {pctLabel(pct)}
    </span>
  )
}

export function AsistenciaF11Tab({
  equipoId,
  lang: langProp,
  readOnly = false,
}: {
  equipoId: string
  lang?: Lang
  readOnly?: boolean
}) {
  const ctxLang = useLang()
  const lang = langProp ?? ctxLang
  const api = useAsistenciaApi()
  const [editMode, setEditMode] = useState(false)
  const [mes, setMes] = useState(mesActualISO())
  const [anadiendoEntrenamiento, setAnadiendoEntrenamiento] = useState(false)
  const [data, setData] = useState<AsistenciaResponse | null>(null)
  const [error, setError] = useState<'forbidden' | 'other' | null>(null)
  const [menu, setMenu] = useState<{ x: number; y: number; sesion: Sesion; miembro: Miembro } | null>(null)
  const [estadoDiaMenu, setEstadoDiaMenu] = useState<{ x: number; y: number; sesion: Sesion } | null>(null)
  const [fichaMiembroId, setFichaMiembroId] = useState<string | null>(null)
  const [dragId, setDragId] = useState<string | null>(null)
  // fix-mobile-responsive: en móvil solo el nombre es columna fija; `#`/Mensual/Anual van dentro de
  // su celda (mismo criterio que `AsistenciaTab`). `leadCols` alimenta los `colSpan` de los resúmenes.
  const isMobile = useIsMobile()
  const leadCols = isMobile ? 1 : 4
  const mesesRef = useScrollActiveIntoView<HTMLElement>('button:disabled', [mes, data])

  // Issue #41, 2026-10-02: F11 se marca desde agosto hasta mayo (antes: enero-diciembre, issue #33).
  const meses = mesesTemporada('f11')

  async function refresh() {
    try {
      const res: AsistenciaResponse = await api.get(`/equipos/${equipoId}/asistencia?mes=${mes}`)
      setData(res)
      setError(null)
    } catch (err) {
      setError(err instanceof ApiError && err.status === 403 ? 'forbidden' : 'other')
    }
  }

  async function handleWriteAction(action: () => Promise<void>) {
    try {
      await action()
    } catch (err) {
      window.alert(t(err instanceof ApiError && err.status === 409 ? 'temporadaCerrada' : 'errorGenerico', lang))
    }
  }

  useEffect(() => {
    refresh()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mes, equipoId])

  if (error === 'forbidden') {
    return (
      <div>
        <h3>{t('sinAcceso', lang)}</h3>
        <p>{t('sinAccesoAsistenciaMensaje', lang)}</p>
      </div>
    )
  }
  if (error === 'other') {
    return <p>{t('errorGenerico', lang)}</p>
  }
  if (!data) return <Spinner />

  async function marcarEstado(sesionId: string, miembroId: string, estado: EstadoF11 | null) {
    await handleWriteAction(async () => {
      await api.patch(`/equipos/${equipoId}/asistencia`, {
        sesion_id: sesionId,
        miembro_equipo_id: miembroId,
        estado,
      })
      await refresh()
    })
  }

  async function handleQuitarSesion(sesionId: string) {
    if (!window.confirm(t('quitarDiaConfirm', lang))) return
    await handleWriteAction(async () => {
      await api.patch(`/equipos/${equipoId}/sesiones/${sesionId}`, { eliminada: true })
      await refresh()
    })
  }

  // Issue #39: reactiva un día festivo/suspendido o añade un entrenamiento extraordinario; el
  // backend decide internamente si crea la sesión o reactiva una ya existente marcada eliminada.
  // 2026-10-23: la fecha llega del diálogo `AddEntrenamientoModal` (antes `window.prompt`, que no
  // funciona en todos los navegadores); el error del API se devuelve para mostrarlo en el diálogo.
  async function handleAnadirEntrenamiento(fecha: string): Promise<string | null> {
    let error: string | null = null
    await handleWriteAction(async () => {
      try {
        await api.post(`/equipos/${equipoId}/sesiones`, { tipo: 'entrenamiento', fecha })
      } catch (err) {
        // Fecha fuera de temporada: el API explica qué falla.
        if (err instanceof ApiError && err.status === 400) {
          error = err.message
          return
        }
        throw err
      }
      await refresh()
    })
    return error
  }

  async function marcarTodos(sesion: Sesion) {
    const sinMarcar = data!.miembros.filter(
      (m) => !m.registros.find((r) => r.sesion_id === sesion.id)?.estado,
    )
    await handleWriteAction(async () => {
      await Promise.all(
        sinMarcar.map((m) =>
          api.patch(`/equipos/${equipoId}/asistencia`, {
            sesion_id: sesion.id,
            miembro_equipo_id: m.id,
            estado: '1',
          }),
        ),
      )
      await refresh()
    })
  }

  async function handleDrop(grupo: string, targetId: string) {
    if (!dragId || dragId === targetId) return
    const miembrosGrupo = data!.miembros.filter((m) => m.grupo === grupo).sort((a, b) => a.orden - b.orden)
    const fromIndex = miembrosGrupo.findIndex((m) => m.id === dragId)
    const toIndex = miembrosGrupo.findIndex((m) => m.id === targetId)
    if (fromIndex === -1 || toIndex === -1) return
    const reordenados = [...miembrosGrupo]
    const [moved] = reordenados.splice(fromIndex, 1)
    reordenados.splice(toIndex, 0, moved)

    await handleWriteAction(async () => {
      await Promise.all(
        reordenados.map((m, index) =>
          api.patch(`/equipos/${equipoId}/miembros/${m.id}`, { orden: index }),
        ),
      )
      await refresh()
    })
    setDragId(null)
  }

  // Issue #80: «+ Jugador / + Entrenador / + Delegado» también en F11 (antes no había alta desde aquí).
  async function handleAddMiembro(nombre: string, grupo: Grupo) {
    await handleWriteAction(async () => {
      await api.post(`/equipos/${equipoId}/miembros`, { nombre, grupo })
      await refresh()
    })
  }

  async function handleExportar() {
    await handleWriteAction(async () => {
      const { blob, filename } = await api.downloadFile(`/equipos/${equipoId}/asistencia/exportar`)
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = filename
      a.click()
      URL.revokeObjectURL(url)
    })
  }

  const sesiones = data.sesiones
  const totalSesiones = sesiones.length
  // Issue #84: los entrenadores ya salen en la tabla, pero las cifras del resumen siguen siendo de
  // los jugadores (como hasta ahora).
  const jugadores = data.miembros.filter((m) => m.grupo === 'con_ficha' || m.grupo === 'sin_ficha')
  const conFichaCount = data.miembros.filter((m) => m.grupo === 'con_ficha').length
  const sinFichaCount = data.miembros.filter((m) => m.grupo === 'sin_ficha').length
  const asistenciasTotal = jugadores.reduce(
    (acc, m) => acc + presenciasF11(m.registros),
    0,
  )
  const mediaGeneral = jugadores.length
    ? Math.round(
        (jugadores.reduce((acc, m) => acc + (m.porcentaje_mes ?? 0), 0) / jugadores.length) * 10,
      ) / 10
    : 0
  const mediaFicha = conFichaCount
    ? Math.round(
        (data.miembros
          .filter((m) => m.grupo === 'con_ficha')
          .reduce((acc, m) => acc + (m.porcentaje_mes ?? 0), 0) /
          conFichaCount) *
          10,
      ) / 10
    : 0

  return (
    <div className={styles.layout}>
      <aside className={styles.sidebar} ref={mesesRef}>
        {meses.map((m) => (
          <button key={m} onClick={() => setMes(m)} disabled={m === mes}>
            {mesLabel(m, lang)}
          </button>
        ))}
      </aside>

      <div>
        <h3>{mesLabel(mes, lang)}</h3>
        <button onClick={handleExportar}>{t('exportarCsv', lang)}</button>
        {!readOnly && (
          <button onClick={() => setEditMode((v) => !v)}>
            {editMode ? t('guardarDias', lang) : t('editarDias', lang)}
          </button>
        )}
        {!readOnly && <button onClick={() => setAnadiendoEntrenamiento(true)}>{tBase('anadirEntrenamiento', lang)}</button>}
          {anadiendoEntrenamiento && (
            <AddEntrenamientoModal
              lang={lang}
              onAdd={handleAnadirEntrenamiento}
              onClose={() => setAnadiendoEntrenamiento(false)}
            />
          )}
        {!readOnly && <AnadirMiembroBotones lang={lang} onAdd={handleAddMiembro} />}

        <dl className="stat-card-grid">
          <div className="stat-card">
            <dt>{t('sesiones', lang)}</dt>
            <dd>{totalSesiones}</dd>
          </div>
          <div className="stat-card">
            <dt>{t('totalTemporada', lang)}</dt>
            <dd>{totalSesiones}</dd>
          </div>
          <div className="stat-card">
            <dt>{t('conFicha', lang)}</dt>
            <dd>{conFichaCount}</dd>
          </div>
          <div className="stat-card">
            <dt>{t('sinFicha', lang)}</dt>
            <dd>{sinFichaCount}</dd>
          </div>
          <div className="stat-card">
            <dt>{t('asistencias', lang)}</dt>
            <dd>{asistenciasTotal}</dd>
          </div>
          <div className="stat-card" style={{ '--stat-color': UMBRAL_COLOR[umbralF11(mediaGeneral)] } as CSSProperties}>
            <dt>{t('mediaGeneral', lang)}</dt>
            <dd>{mediaGeneral}%</dd>
          </div>
          <div className="stat-card" style={{ '--stat-color': UMBRAL_COLOR[umbralF11(mediaFicha)] } as CSSProperties}>
            <dt>{t('mediaFicha', lang)}</dt>
            <dd>{mediaFicha}%</dd>
          </div>
        </dl>

        {/* Look & feel (club, 2026-10-23: "la palabra leyenda ahí en medio queda fatal"): el desplegable
            es un botón con el mismo relieve que el resto y la leyenda se abre en una tarjeta. */}
        <details className={styles.leyendaPanel}>
          <summary>
            <span aria-hidden>ⓘ</span> {t('leyenda', lang)}
          </summary>
          <div className={styles.leyendaContenido}>
            <p>{t('leyendaExplicacionComputo', lang)}</p>
            <ul className={styles.leyenda}>
              {ESTADOS_F11.map((estado) => (
                <li key={estado}>
                  <EstadoChip estado={estado} /> {estadoLabel(estado, lang)}
                </li>
              ))}
              <li>
                <span className={styles.leyendaSinMarca} aria-hidden /> {t('sinMarcar', lang)}
              </li>
            </ul>
          </div>
        </details>

        {/* Look & feel (club, 2026-10-23): cabecera de fechas fija al hacer scroll vertical (`sticky-head`). */}
        <div className={`${styles.tableWrap} sticky-head`}>
        <table className={`${styles.table} ${isMobile ? styles.mobile : ''}`}>
          <thead>
            <tr>
              {!isMobile && (
                <>
                  <th>#</th>
                  <th>{t('porcMes', lang)}</th>
                  <th>{t('porcAno', lang)}</th>
                </>
              )}
              <th>{t('jugador', lang)}</th>
              {sesiones.map((sesion) => (
                <th key={sesion.id} className={sesion.tipo === 'partido' ? styles.headerPartido : undefined}>
                  {/* Feedback del club, 2026-10-05: "deberían verse todos igual que en f11, pero con el
                      día de la semana en cabecera". 2026-10-23: el nº de sesión (o ⚽) va debajo del día
                      en vez de mezclado entre el día de la semana y el número (ver `DiaHeader`). */}
                  <DiaHeader
                    semana={diaSemanaLabel(fechaCorta(sesion.fecha), lang)}
                    dia={diaNumero(sesion.fecha)}
                    extra={sesion.tipo === 'partido' ? '⚽' : sesion.numero != null ? `#${sesion.numero}` : null}
                    onOpen={readOnly ? undefined : (e) => setEstadoDiaMenu({ x: e.clientX, y: e.clientY, sesion })}
                  />
                </th>
              ))}
              <th>TOT</th>
              {CONTADOR_COLS.map((c) => (
                <th key={c}>{c}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {(() => {
              // Issue #46, 2026-10-02: numeración correlativa 1..N a lo largo de todo el equipo,
              // continuando a través de los tres grupos (Con Ficha / Sin Ficha / Entrenadores), no
              // reiniciando en cada uno.
              let numeroGlobal = 0
              return GRUPOS.map(({ key, labelKey }) => {
              const miembrosGrupo = data.miembros.filter((m) => m.grupo === key).sort((a, b) => a.orden - b.orden)
              if (miembrosGrupo.length === 0) return null

              const totalGrupo = miembrosGrupo.reduce(
                (acc, m) => acc + presenciasF11(m.registros),
                0,
              )
              const mediaGrupo = miembrosGrupo.length
                ? Math.round((miembrosGrupo.reduce((acc, m) => acc + (m.porcentaje_mes ?? 0), 0) / miembrosGrupo.length) * 10) / 10
                : 0

              return (
                <Fragment key={key}>
                  <tr key={`${key}-header`}>
                    <td colSpan={leadCols + 1 + sesiones.length + 1 + CONTADOR_COLS.length}>
                      <strong>
                        {t(labelKey, lang)} ({miembrosGrupo.length})
                      </strong>
                    </td>
                  </tr>
                  {miembrosGrupo.map((miembro) => {
                    numeroGlobal += 1
                    return (
                    <tr
                      key={miembro.id}
                      draggable
                      onDragStart={() => setDragId(miembro.id)}
                      onDragOver={(e) => e.preventDefault()}
                      onDrop={() => handleDrop(key, miembro.id)}
                    >
                      {!isMobile && (
                        <>
                          <td>{numeroGlobal}</td>
                          <td>
                            <PctPill pct={miembro.porcentaje_mes} />
                          </td>
                          <td>
                            <PctPill pct={miembro.porcentaje_ano} />
                          </td>
                        </>
                      )}
                      {/* Safari: con `display: flex` en el propio <td> deja de ser celda de tabla y el `sticky`
                          queda confinado en una celda anónima — no se fija. El flex va en un div interior. */}
                      <td className={styles.playerTd}>
                        <div className={styles.playerCell}>
                          <span className={styles.playerAvatar}>
                            {miembro.persona.foto_url ? (
                              <img src={miembro.persona.foto_url} alt="" width={32} height={32} />
                            ) : (
                              <span aria-hidden>{miembro.persona.nombre.slice(0, 2).toUpperCase()}</span>
                            )}
                          </span>
                          {/* Issue #60, 2026-10-06: "que se vea siempre el nombre completo... si no
                              cabe, nombre arriba y apellido debajo", en vez de cortar con "…" —
                              texto normal, que haga wrap de forma natural en el espacio entre nombre
                              y apellidos cuando no quepa en una línea (ver `.playerName`). */}
                          <span className={styles.playerInfo}>
                            <button className={styles.playerName} onClick={() => setFichaMiembroId(miembro.id)}>
                              {isMobile && <span className={styles.numero}>{numeroGlobal}.</span>}
                              {miembro.persona.nombre}
                            </button>
                            {isMobile && (
                              <span className={styles.mobilePcts}>
                                <span>
                                  {t('porcMes', lang)} <PctPill pct={miembro.porcentaje_mes} />
                                </span>
                                <span>
                                  {t('porcAno', lang)} <PctPill pct={miembro.porcentaje_ano} />
                                </span>
                              </span>
                            )}
                          </span>
                        </div>
                      </td>
                      {sesiones.map((sesion) => {
                        const registro = miembro.registros.find((r) => r.sesion_id === sesion.id)
                        if (!registro) return <td key={sesion.id} />
                        return (
                          <td
                            key={sesion.id}
                            className={styles.cell}
                            onClick={(e) =>
                              !readOnly && setMenu({ x: e.clientX, y: e.clientY, sesion, miembro })
                            }
                          >
                            {registro.estado ? <EstadoChip estado={registro.estado as EstadoF11} /> : '·'}
                          </td>
                        )
                      })}
                      <td>{presenciasF11(miembro.registros)}</td>
                      {CONTADOR_COLS.map((c) => (
                        <td key={c}>{miembro.contadores[c] || ''}</td>
                      ))}
                    </tr>
                    )
                  })}
                  <tr key={`${key}-total`}>
                    <td colSpan={leadCols}>{t('total', lang)}</td>
                    <td colSpan={sesiones.length} />
                    <td>{totalGrupo}</td>
                  </tr>
                  <tr key={`${key}-media`}>
                    <td colSpan={leadCols}>{t('media', lang)}</td>
                    <td colSpan={sesiones.length} />
                    <td>{mediaGrupo}%</td>
                  </tr>
                </Fragment>
              )
              })
            })()}
            <tr>
              <td colSpan={leadCols}>
                <strong>{t('totalGeneral', lang)}</strong>
              </td>
              <td colSpan={sesiones.length} />
              <td>{asistenciasTotal}</td>
            </tr>
          </tbody>
        </table>
        </div>
      </div>

      {estadoDiaMenu && (
        <EstadoDiaMenu
          x={estadoDiaMenu.x}
          y={estadoDiaMenu.y}
          lang={lang}
          onTodos={() => marcarTodos(estadoDiaMenu.sesion)}
          onFestivo={() => handleQuitarSesion(estadoDiaMenu.sesion.id)}
          onSuspendido={() => handleQuitarSesion(estadoDiaMenu.sesion.id)}
          onClose={() => setEstadoDiaMenu(null)}
        />
      )}

      {menu && (
        <ContextMenuEstado
          x={menu.x}
          y={menu.y}
          actual={menu.miembro.registros.find((r) => r.sesion_id === menu.sesion.id)?.estado ?? null}
          lang={lang}
          onSelect={(estado) => {
            marcarEstado(menu.sesion.id, menu.miembro.id, estado)
            setMenu(null)
          }}
          onClose={() => setMenu(null)}
        />
      )}

      {fichaMiembroId && (
        <FichaOverlayF11
          equipoId={equipoId}
          miembroId={fichaMiembroId}
          lang={lang}
          onClose={() => setFichaMiembroId(null)}
          onChanged={refresh}
        />
      )}
    </div>
  )
}
